<template>
  <div>
    <el-card shadow="hover">
      <template slot="header">
        <div class="clearfix">
          <span>入库管理</span>
          <div style="float: right">
            <el-button type="success" size="small" @click="handleExport" :loading="exporting">导出Excel</el-button>
            <el-button type="primary" size="small" @click="openCreateDialog">新建入库</el-button>
          </div>
        </div>
      </template>

      <el-form :inline="true" :model="searchForm" class="mb-4">
        <el-form-item label="商品名称">
          <el-input v-model="searchForm.productName" placeholder="请输入" style="width: 180px" @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="商品分类">
          <el-select v-model="searchForm.categoryId" placeholder="全部" clearable style="width: 160px" @change="handleSearch">
            <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="入库日期">
          <el-date-picker v-model="searchForm.dateRange" type="daterange" unlink-panels value-format="yyyy-MM-dd"
                          range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 380px"></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="list" style="width: 100%" stripe size="small" border @row-dblclick="row => openEditDialog(row)">
        <el-table-column type="expand" width="40" fixed="left">
          <template slot-scope="props">
            <div class="detail-panel dbl-edit" @dblclick="openEditDialog(props.row)" title="双击任意位置修改">
              <div class="detail-panel-title"><i class="el-icon-info"></i><span>入库详情</span></div>
              <div class="detail-card-row">
                <div class="detail-card-body cols-4">
                  <div class="dc-cell"><span class="dc-label">入库ID</span><span class="dc-value">{{ props.row.id }}</span></div>
                  <div class="dc-cell"><span class="dc-label">入库日期</span><span class="dc-value">{{ String(props.row.createTime || '').slice(0, 10) || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">入库来源</span><span class="dc-value">{{ props.row.source || '手工入库' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">商品分类</span>
                    <el-tag :type="isGiftCategory(rowCategoryId(props.row)) ? 'warning' : 'primary'" size="mini">
                      {{ rowCategoryName(props.row) }}
                    </el-tag>
                  </div>
                  <div class="dc-cell"><span class="dc-label">商品名称</span><span class="dc-value">{{ props.row.productName || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">数量</span><span class="dc-value">{{ props.row.quantity || 0 }}</span></div>
                  <div class="dc-cell"><span class="dc-label">已累计入库</span><span class="dc-value">{{ productInboundCount(props.row.productId) }}</span></div>
                  <div class="dc-cell"><span class="dc-label">接货人</span><span class="dc-value">{{ props.row.receiver || '-' }}</span></div>
                  <div class="dc-cell"><span class="dc-label">生产批次</span><span class="dc-value">{{ props.row.batchNo || '-' }}</span></div>
                  <div class="dc-cell w4"><span class="dc-label">入库检测报告</span>
                    <template v-if="reportUrls(props.row.agreementFile).length">
                      <span class="file-thumbs">
                        <template v-for="(u, i) in reportUrls(props.row.agreementFile)">
                          <el-image v-if="isImage(u)" :key="'simg'+i" :src="full(u)"
                                    :preview-src-list="reportUrls(props.row.agreementFile).filter(x => isImage(x)).map(x => full(x))"
                                    fit="cover" class="file-thumb-sm"></el-image>
                          <a v-else :key="'sa'+i" :href="full(u)" target="_blank" rel="noopener" class="file-link-sm">{{ fileName(u) }}</a>
                        </template>
                      </span>
                    </template>
                    <span v-else class="muted">未扫描</span>
                  </div>
                  <div class="dc-cell w4"><span class="dc-label">备注</span><span class="dc-value">{{ displayRemark(props.row.remark) || '-' }}</span></div>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="入库日期" width="120">
          <template slot-scope="s">{{ String(s.row.createTime || '').slice(0, 10) }}</template>
        </el-table-column>
        <el-table-column label="商品分类" width="110">
          <template slot-scope="s">
            <el-tag :type="isGiftCategory(rowCategoryId(s.row)) ? 'warning' : 'primary'" size="mini">
              {{ rowCategoryName(s.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="商品名称" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="quantity" label="数量" width="90"></el-table-column>
        <el-table-column prop="batchNo" label="生产批次" width="140">
          <template slot-scope="s">{{ s.row.batchNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="已累计入库" width="110" align="center">
          <template slot-scope="s">{{ productInboundCount(s.row.productId) }}</template>
        </el-table-column>
        <el-table-column prop="receiver" label="接货人" width="100"></el-table-column>
        <el-table-column label="备注" min-width="160" show-overflow-tooltip>
          <template slot-scope="s">{{ displayRemark(s.row.remark) }}</template>
        </el-table-column>
        <el-table-column prop="id" label="入库ID" width="80"></el-table-column>
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <template slot-scope="s">
            <el-button type="primary" size="mini" @click="openEditDialog(s.row)">修改</el-button>
            <el-button
              v-if="canDeleteRow(s.row)"
              type="danger"
              size="mini"
              @click="handleDelete(s.row)"
            >删除</el-button>
            <el-tooltip v-else content="客户退货入库等系统业务记录不可删除" placement="top">
              <span style="margin-left: 10px;">
                <el-button type="danger" size="mini" disabled>删除</el-button>
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-4">
        <el-pagination
          @size-change="s => { pageSize = s; loadList() }"
          @current-change="p => { currentPage = p; loadList() }"
          :current-page="currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper" :total="total"></el-pagination>
      </div>
    </el-card>

    <el-dialog :title="dialogMode === 'edit' ? '修改入库记录' : '新建入库'" :visible.sync="dialogVisible" width="880px" :close-on-click-modal="false" :close-on-press-escape="false">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px" size="small">
        <el-row :gutter="24">
          <el-col :span="12"><el-form-item label="商品分类" prop="categoryId">
            <el-select v-model="form.categoryId" placeholder="请选择商品分类" style="width: 100%" @change="onCategoryChange">
              <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id"></el-option>
            </el-select>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="商品" prop="productId">
            <el-select v-model="form.productId" filterable placeholder="先选择分类" style="width: 100%" :disabled="!form.categoryId">
              <el-option v-for="p in productOptionsByCategory" :key="p.id" :label="p.name + '（库存：' + (p.stock || 0) + '）'" :value="p.id"></el-option>
            </el-select>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12"><el-form-item label="数量" prop="quantity">
            <el-input-number v-model="form.quantity" :min="1" style="width: 100%"></el-input-number>
          </el-form-item></el-col>
          <el-col :span="12" v-if="!isGiftCategory(form.categoryId)"><el-form-item label="生产批次" prop="batchNo" required>
            <el-input v-model="form.batchNo" placeholder="请输入生产批次"></el-input>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12"><el-form-item label="接货人">
            <el-input v-model="form.receiver" placeholder="请输入接货人"></el-input>
          </el-form-item></el-col>
          <el-col :span="12"><el-form-item label="入库时间">
            <el-date-picker v-model="form.createTime" type="date" value-format="yyyy-MM-dd" placeholder="选择入库时间" style="width: 100%"></el-date-picker>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="24"><el-form-item label="入库检测报告">
            <el-button size="small" type="primary" icon="el-icon-camera" @click="scanVisible = true">入库检测报告</el-button>
            <span class="muted small" style="margin-left:10px;color:#909399;font-size:12px;">单个文件最大 100 MB</span>
            <div v-if="reportUrls(form.agreementFile).length" class="agreement-preview">
              <div v-for="(u, i) in reportUrls(form.agreementFile)" :key="'rep'+i" class="agreement-item">
                <el-image
                  v-if="isImage(u)"
                  :src="full(u)"
                  :preview-src-list="reportUrls(form.agreementFile).filter(x => isImage(x)).map(x => full(x))"
                  style="width:80px;height:80px;border-radius:4px;margin-right:10px"
                  fit="cover"></el-image>
                <el-link v-else type="primary" :href="full(u)" target="_blank" rel="noopener" icon="el-icon-document">{{ fileName(u) }}</el-link>
                <el-button size="mini" type="text" style="color:#f56c6c;margin-left:10px" @click="removeAgreement(i)">移除</el-button>
              </div>
            </div>
            <span v-else class="muted small" style="margin-left:8px">未扫描（可选）</span>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="24"><el-form-item label="备注">
            <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="选填"></el-input>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24" v-if="dialogMode === 'edit' && quantityChanged">
          <el-col :span="24"><el-form-item label="变更原因">
            <el-input v-model="form.quantityChangeRemark" placeholder="数量发生变化时建议填写原因（选填）" maxlength="200" show-word-limit></el-input>
          </el-form-item></el-col>
        </el-row>
        <el-row :gutter="24" v-if="dialogMode === 'edit'">
          <el-col :span="24"><el-form-item label="数量修改记录">
            <div v-if="quantityLogs.length" class="qty-log-list">
              <div v-for="(g, i) in quantityLogs" :key="g.id || i" class="qty-log-item">
                <span class="qty-log-time">{{ formatDate(g.changeTime) }}</span>
                <span class="qty-log-change">{{ g.oldQuantity }} → {{ g.newQuantity }}</span>
                <span class="qty-log-delta" :class="g.delta >= 0 ? 'inc' : 'dec'">{{ g.delta >= 0 ? '+' + g.delta : g.delta }}</span>
                <span class="qty-log-op">操作人：{{ g.operator || '-' }}</span>
                <span v-if="g.remark" class="qty-log-remark">{{ g.remark }}</span>
              </div>
            </div>
            <span v-else class="muted">暂无修改记录</span>
          </el-form-item></el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="success" @click="handleSubmit" :loading="submitting">保存</el-button>
      </div>
    </el-dialog>

    <gaopaiyi-scanner :visible.sync="scanVisible" biz-type="agreement" @uploaded="onAgreementScanned"></gaopaiyi-scanner>
    <agreement-change-log-drawer :visible.sync="agrLogVisible" biz-type="INVENTORY" :biz-id="agrLogId" title="公司入库入库检测报告变更记录"></agreement-change-log-drawer>
  </div>
</template>

<script>
import axios from 'axios'
import { inventoryApi, productApi, categoryApi } from '../api/api'
import GaopaiyiScanner from '../components/GaopaiyiScanner.vue'
import AgreementChangeLogDrawer from '../components/AgreementChangeLogDrawer.vue'

export default {
  name: 'InboundManagement',
  components: { GaopaiyiScanner, AgreementChangeLogDrawer },
  data() {
    return {
      scanVisible: false,
      agrLogVisible: false,
      agrLogId: null,
      list: [],
      categoryList: [],
      allProducts: [],
      searchForm: { productName: '', categoryId: null, dateRange: null },
      currentPage: 1,
      pageSize: 10,
      total: 0,
      dialogVisible: false,
      dialogMode: 'create',
      form: {},
      rules: {
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        productId: [{ required: true, message: '请选择商品', trigger: 'change' }],
        quantity: [{ required: true, message: '请输入数量', trigger: 'blur' }],
        batchNo: [{ validator: (rule, value, cb) => {
          // 礼品类目可不填；其它类目必填
          if (this.isGiftCategory(this.form.categoryId)) return cb()
          if (value == null || String(value).trim() === '') return cb(new Error('请输入生产批次'))
          cb()
        }, trigger: 'blur' }]
      },
      exporting: false,
      submitting: false,
      originalQuantity: null,
      quantityLogs: []
    }
  },
  computed: {
    productOptionsByCategory() {
      if (!this.form.categoryId) return []
      return this.allProducts.filter(p => p.categoryId === this.form.categoryId)
    },
    quantityChanged() {
      return this.dialogMode === 'edit' && this.originalQuantity != null && Number(this.form.quantity) !== Number(this.originalQuantity)
    }
  },
  mounted() {
    this.loadCategories()
    this.loadAllProducts()
    this.loadList()
  },
  methods: {
    loadCategories() {
      categoryApi.getCategoryList().then(res => { this.categoryList = res.data || [] })
    },
    loadAllProducts() {
      productApi.getProductList(true).then(res => { this.allProducts = res.data || [] })
    },
    isGiftCategory(id) {
      const c = this.categoryList.find(x => x.id === id)
      return !!(c && ['Gift', '礼品'].includes(c.name))
    },
    isGiftRow(row) {
      return this.isGiftCategory(this.productCategoryId(row.productId))
    },
    productCategoryId(productId) {
      const p = this.allProducts.find(x => x.id === productId)
      return p ? p.categoryId : null
    },
    productInboundCount(productId) {
      const p = this.allProducts.find(x => x.id === productId)
      return p && p.inboundCount != null ? p.inboundCount : 0
    },
    productCategoryName(productId) {
      const cid = this.productCategoryId(productId)
      const c = this.categoryList.find(x => x.id === cid)
      return c ? c.name : '-'
    },
    /** 流水行的分类：产品被删时按 productName 前缀推断（「礼品-」→ 礼品；其它 → 产品），与后端 all-stock 孤儿块一致 */
    rowCategoryId(row) {
      if (row && row.productId) {
        const p = this.allProducts.find(x => x.id === row.productId)
        if (p) return p.categoryId
      }
      const name = (row && row.productName) || ''
      const giftCat = this.categoryList.find(c => c.name === '礼品' || c.name === 'Gift')
      const productCat = this.categoryList.find(c => c.name === '产品')
      if (name.startsWith('礼品-') && giftCat) return giftCat.id
      return productCat ? productCat.id : null
    },
    rowCategoryName(row) {
      const cid = this.rowCategoryId(row)
      const c = this.categoryList.find(x => x.id === cid)
      return c ? c.name : '-'
    },
    loadList() {
      const params = { type: '入库' }
      if (this.searchForm.productName) params.productName = this.searchForm.productName
      if (this.searchForm.categoryId) params.categoryId = this.searchForm.categoryId
      if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
        params.startDate = this.searchForm.dateRange[0]
        params.endDate = this.searchForm.dateRange[1]
      }
      inventoryApi.getInventoryPage(this.currentPage, this.pageSize, params).then(res => {
        this.list = res.data.records || []
        this.total = res.data.total || this.list.length
      })
    },
    handleSearch() { this.currentPage = 1; this.loadList() },
    resetSearch() { this.searchForm = { productName: '', categoryId: null, dateRange: null }; this.handleSearch() },
    // 销售订单自动入库/库存回滚产生的备注已不再使用，列表里不展示这类内容
    displayRemark(remark) {
      const r = (remark == null ? '' : String(remark)).trim()
      if (/^销售订单#?\d*\s*(自动入库|库存回滚)/.test(r)) return ''
      return r
    },
    nowStr() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    openCreateDialog() {
      this.dialogMode = 'create'
      this.form = { categoryId: null, productId: '', quantity: 1, batchNo: '', inspectionReport: '', agreementFile: '', receiver: '', remark: '', createTime: this.nowStr() }
      this.originalQuantity = null
      this.quantityLogs = []
      this.dialogVisible = true
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    },
    openEditDialog(row) {
      this.dialogMode = 'edit'
      const createTime = row.createTime ? String(row.createTime).replace('T', ' ').slice(0, 10) : this.nowStr()
      this.form = { ...row, categoryId: this.productCategoryId(row.productId), createTime, quantityChangeRemark: '' }
      this.originalQuantity = row.quantity
      this.quantityLogs = []
      this.dialogVisible = true
      this.loadQuantityLogs(row.id)
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    },
    loadQuantityLogs(id) {
      inventoryApi.getQuantityLogs(id).then(res => {
        this.quantityLogs = Array.isArray(res.data) ? res.data : (res.data && res.data.data) || []
      }).catch(() => { this.quantityLogs = [] })
    },
    canDeleteRow(row) {
      if (!row) return false
      const src = row.source ? String(row.source).trim() : ''
      return !src || src === '手工入库'
    },
    handleDelete(row) {
      const qty = row.quantity || 0
      const name = row.productName || '该商品'
      this.$confirm(
        `确定要删除该入库记录吗？<br/><span style="color:#E6A23C;font-size:12px;">提示：删除后将从【${name}】扣减库存 ${qty} 件，且不可撤销。</span>`,
        '删除确认',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(() => {
        inventoryApi.deleteRecord(row.id).then(res => {
          if (res.data && res.data.success) {
            this.$message.success('删除成功')
            this.loadList()
            this.loadAllProducts()
          } else {
            this.$message.error((res.data && res.data.message) || '删除失败')
          }
        }).catch(err => {
          this.$message.error((err.response && err.response.data && err.response.data.message) || '删除失败')
        })
      }).catch(() => {})
    },
    onCategoryChange() {
      this.form.productId = ''
      if (this.isGiftCategory(this.form.categoryId)) {
        this.form.inspectionReport = ''
      }
    },
    reportUrls(val) {
      if (!val) return []
      return String(val).split(',').map(s => s.trim()).filter(Boolean)
    },
    isImage(url) {
      if (!url) return false
      const u = String(url).split('?')[0].toLowerCase()
      return /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(u)
    },
    fileName(url) {
      if (!url) return ''
      const s = String(url).split('?')[0]
      const idx = s.lastIndexOf('/')
      return idx >= 0 ? s.substring(idx + 1) : s
    },
    imagePreviewList(val) {
      return this.reportUrls(val).filter(u => this.isImage(u)).map(u => this.full(u))
    },
    removeReport(index) {
      const arr = this.reportUrls(this.form.inspectionReport)
      arr.splice(index, 1)
      this.$set(this.form, 'inspectionReport', arr.join(','))
    },
    onAgreementScanned(url) {
      const arr = this.reportUrls(this.form.agreementFile)
      arr.push(url)
      this.$set(this.form, 'agreementFile', arr.join(','))
    },
    openAgrLog(row) {
      this.agrLogId = row.id
      this.agrLogVisible = true
    },
    removeAgreement(index) {
      const arr = this.reportUrls(this.form.agreementFile)
      arr.splice(index, 1)
      this.$set(this.form, 'agreementFile', arr.join(','))
    },
    handleReportChange(file) {
      if (!file.raw) return
      const fd = new FormData()
      fd.append('file', file.raw)
      axios.post('/file/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => {
        if (res.data && res.data.success) {
          const arr = this.reportUrls(this.form.inspectionReport)
          arr.push(res.data.url)
          this.$set(this.form, 'inspectionReport', arr.join(','))
          this.$message.success('上传成功')
        } else {
          this.$message.error((res.data && res.data.message) || '上传失败')
        }
      })
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const req = this.dialogMode === 'edit'
          ? inventoryApi.updateRecord(this.form.id, {
              productId: this.form.productId,
              quantity: this.form.quantity,
              quantityChangeRemark: this.form.quantityChangeRemark,
              batchNo: this.form.batchNo,
              inspectionReport: this.form.inspectionReport,
              agreementFile: this.form.agreementFile,
              receiver: this.form.receiver,
              remark: this.form.remark,
              createTime: this.form.createTime
            })
          : inventoryApi.inStockAny(this.form)
        req.then(res => {
          if (res.data && res.data.success) {
            this.$message.success(this.dialogMode === 'edit' ? '修改成功' : '入库成功')
            this.dialogVisible = false
            this.loadList()
            if (this.dialogMode === 'create') this.loadAllProducts()
          } else {
            this.$message.error((res.data && res.data.message) || '操作失败')
          }
        }).finally(() => { this.submitting = false })
      })
    },
    handleExport() {
      this.exporting = true
      const params = { type: '入库' }
      if (this.searchForm.productName) params.productName = this.searchForm.productName
      if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
        params.startDate = this.searchForm.dateRange[0]
        params.endDate = this.searchForm.dateRange[1]
      }
      if (this.searchForm.categoryId) {
        const isGift = this.isGiftCategory(this.searchForm.categoryId)
        params.productType = isGift ? 'gift' : 'product'
      }
      inventoryApi.exportInbound(params).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = '入库列表_' + new Date().toISOString().slice(0, 10) + '.xlsx'
        link.click()
      }).finally(() => { this.exporting = false })
    },
    full(url) {
      if (!url) return ''
      if (/^(https?:|data:)/i.test(url)) return url
      // 跟随 axios baseURL：本地→localhost:8090，线上→chb.bjzkbh.com
      const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
      return base + url
    },
    formatDate(v) { return v ? String(v).replace('T', ' ').slice(0, 19) : '' }
  }
}
</script>

<style scoped>
.mb-4 { margin-bottom: 16px; }
.mt-4 { margin-top: 16px; text-align: right; }
.muted { color: #909399; font-size: 12px; }
.link { color: #409eff; cursor: pointer; }
.small { font-size: 12px; margin-left: 8px; }
.report-thumbs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.report-thumb { width: 64px; height: 64px; border-radius: 4px; border: 1px solid #ebeef5; cursor: pointer; background: #fff; }
.dbl-edit { cursor: pointer; transition: background .15s; }
.dbl-edit:hover { background: #f0f7ff; }
.report-file { display: inline-flex; align-items: center; height: 64px; padding: 0 8px; border: 1px dashed #dcdfe6; border-radius: 4px; background: #fff; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.form-report-thumbs { margin-top: 8px; }
.report-thumb-item { position: relative; }
.report-thumb-close { position: absolute; top: -6px; right: -6px; width: 18px; height: 18px; line-height: 18px; text-align: center; border-radius: 50%; background: #f56c6c; color: #fff; font-size: 12px; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
.report-thumb-item:hover .report-thumb-close { background: #f78989; }
.qty-log-list { max-height: 180px; overflow-y: auto; border: 1px solid #ebeef5; border-radius: 4px; background: #fafcff; padding: 6px 10px; }
.qty-log-item { display: flex; align-items: center; gap: 12px; padding: 4px 0; font-size: 12px; border-bottom: 1px dashed #ebeef5; }
.qty-log-item:last-child { border-bottom: none; }
.qty-log-time { color: #909399; min-width: 140px; }
.qty-log-change { color: #303133; font-weight: 600; min-width: 90px; }
.qty-log-delta { font-weight: 600; min-width: 50px; }
.qty-log-delta.inc { color: #67c23a; }
.qty-log-delta.dec { color: #f56c6c; }
.qty-log-op { color: #606266; }
.qty-log-remark { color: #909399; }

.detail-panel { padding: 14px 24px 18px; background: linear-gradient(180deg,#fafcff 0%,#f5f7fa 100%); border-top: 1px dashed #dcdfe6; }
.detail-panel-title { display: flex; align-items: center; gap: 6px; color: #303133; font-size: 13px; font-weight: 600; margin: 4px 0 12px; }
.detail-panel-title i { color: #409eff; }
.detail-card-row { background: #fff; border: 1px solid #ebeef5; border-radius: 6px; box-shadow: 0 1px 2px rgba(0,0,0,0.02); padding: 12px 14px; margin-bottom: 12px; }
.detail-card-body { display: grid; gap: 8px; }
.detail-card-body.cols-4 { grid-template-columns: repeat(4, 1fr); }
.dc-cell { display: flex; justify-content: flex-start; align-items: center; gap: 8px; padding: 6px 10px; background: #f8f9fb; border-radius: 4px; }
.dc-cell.w4 { grid-column: span 4; }
.dc-label { color: #909399; font-size: 12px; flex-shrink: 0; }
.dc-label::after { content: '：'; }
.dc-value { color: #303133; font-size: 13px; font-weight: 500; }
/* 展开行附件缩略图 / 文件链接（与销售订单"附件"一致） */
.file-thumbs { display: inline-flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.file-thumb-sm { width: 40px; height: 40px; border-radius: 4px; }
.file-link-sm { font-size: 12px; color: #409eff; text-decoration: none; padding: 2px 8px; background: #ecf5ff; border-radius: 3px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 弹窗里附件预览（与销售订单"附件"一致） */
.agreement-preview { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-top: 8px; }
.agreement-preview .agreement-item { display: flex; align-items: center; }
</style>
